import React, { useState } from 'react';
import { Box, Drawer, IconButton, List, ListItem, styled } from '@mui/material';
import { Toc, CloseRounded } from '@mui/icons-material';
import { AssessmentsPageIndicator } from 'components/AssessmentsPageIndicator/AssessmentsPageIndicator';
import { AssessmentsPage } from 'features/assessments/types';

interface MobileAssessmentsPageNavigationProps {
  /**
   * Current page number
   */
  currentPage: number;
  /**
   * Array of page data with question counts
   */
  pages: Array<AssessmentsPage>;
  /**
   * Callback when a page indicator is clicked
   */
  onPageClick: (page: number) => void;
  /**
   * Optional styles for the menu button
   */
  menuButtonStyle?: React.CSSProperties;
}

const MenuButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: theme.spacing(2),
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  '& .MuiDrawer-paper': {
    width: '70%',
    maxWidth: 400,
    borderTopRightRadius: theme.spacing(1.5),
    borderBottomRightRadius: theme.spacing(1.5),
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  backgroundColor: theme.palette.common.white,
  zIndex: 1,
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.grey[900],
  padding: theme.spacing(1.5),
}));

const PageList = styled(List)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(0, 3, 3, 3),
}));

const PageListItem = styled(ListItem)(() => ({
  padding: 0,
}));

export const MobileAssessmentsPageNavigation = ({
  currentPage,
  pages,
  onPageClick,
  menuButtonStyle,
}: MobileAssessmentsPageNavigationProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handlePageClick = (page: number) => {
    onPageClick(page);
    handleCloseDrawer();
  };

  return (
    <>
      <MenuButton
        id="assessment__mobile-page-navigation-menu-button"
        onClick={handleOpenDrawer}
        aria-label="Open page navigation menu"
        size="large"
        style={menuButtonStyle}
      >
        <Toc />
      </MenuButton>

      <StyledDrawer
        id="assessment__mobile-page-navigation-drawer"
        anchor="left"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
      >
        <DrawerHeader>
          <CloseButton
            id="assessment__mobile-page-navigation-close-button"
            onClick={handleCloseDrawer}
            aria-label="Close page navigation menu"
            size="large"
          >
            <CloseRounded />
          </CloseButton>
        </DrawerHeader>

        <PageList>
          {pages.map((page) => (
            <PageListItem key={page.pageNumber}>
              <AssessmentsPageIndicator
                pageNumber={page.pageNumber}
                questionCount={page.questionCount}
                questionsCompleted={page.questionsCompleted}
                isActive={page.pageNumber === currentPage}
                onClick={handlePageClick}
              />
            </PageListItem>
          ))}
        </PageList>
      </StyledDrawer>
    </>
  );
};
